"use client";
import React, { useEffect, useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import Link from "next/link";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@radix-ui/react-dropdown-menu";
import { GlobeIcon } from "@radix-ui/react-icons";
import { useLocale } from "next-intl";
import { useTransition } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import trq_logo from "@/public/logo_glassmorphism.png";
import { useTranslatedData } from "@/data";
import BurgerMenu from "../burgerMenu";

export const FloatingNav = ({ className }: { className?: string }) => {
  const { scrollYProgress } = useScroll();
  const [visible, setVisible] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [isPending, startTransition] = useTransition();
  const localActive = useLocale();
  const router = useRouter();

  // 👇 grab navItems from translations
  const { navItems } = useTranslatedData();

  // Check if mobile view
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useMotionValueEvent(scrollYProgress, "change", (current) => {
    if (typeof current === "number") {
      let direction = current - scrollYProgress.getPrevious()!;
      if (scrollYProgress.get() < 0.05) {
        setVisible(true);
      } else {
        setVisible(direction < 0);
      }
    }
  });

  useEffect(() => {
    setIsClient(true);
  }, []);

  const toggleLanguage = (nextLocale: string) => {
    startTransition(() => {
      router.replace(`/${nextLocale}`);
    });
  };

  if (!isClient) return null;

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 1, y: -100 }}
          animate={{ y: visible ? 0 : -100, opacity: visible ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "flex max-w-fit md:min-w-[70vw] lg:min-w-fit fixed z-[5000] top-10 inset-x-0 mx-auto px-10 py-5 rounded-lg border border-black/.1 shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)] items-center justify-center space-x-4",
            "w-[90vw] px-6 py-4",
            className,
          )}
          style={{
            backdropFilter: "blur(16px) saturate(180%)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.125)",
          }}
        >
          {/* Logo */}
          <video
            src="/trq_liquid.webm"
            onClick={() => router.push("/")}
            height={53}
            width={53}
            autoPlay
            loop
            muted
            playsInline
            className="center cursor-pointer"
            style={{ paddingLeft: "3px" }}
          />
          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              {/* 🌍 Language switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center p-2 text-neutral-600 dark:text-neutral-50 hover:text-neutral-500 dark:hover:text-neutral-300"
                    aria-label="Change language"
                  >
                    <GlobeIcon className="w-6 h-6" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 mt-2">
                  <div className="flex flex-col items-center">
                    <DropdownMenuItem
                      className="w-full text-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                      onClick={() => toggleLanguage("en")}
                    >
                      English
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="w-full text-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                      onClick={() => toggleLanguage("ar")}
                    >
                      عربي
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {navItems
                .filter((navItem) => !navItem.link.includes("/gallery"))
                .map((navItem, idx) => {
                  const isProjects = navItem.link.includes("/gallery");

                  return (
                    <Link
                      key={`link-${idx}`}
                      href={navItem.link}
                      onClick={(e) => {
                        if (isProjects) {
                          e.preventDefault();
                          window.location.href = navItem.link;
                        }
                      }}
                      className={cn(
                        "relative dark:text-neutral-50 flex items-center space-x-1 text-neutral-600 dark:hover:text-neutral-300 hover:text-neutral-500",
                      )}
                      style={{ paddingRight: "20px" }}
                    >
                      <span className="text-sm !cursor-pointer">
                        {navItem.name}
                      </span>
                    </Link>
                  );
                })}
            </>
          )}

          {/* Mobile menu */}
          {isMobile && (
            <div
              className="flex-1 flex justify-end"
              style={{
                width: "100vh",
                paddingRight: "20px",
              }}
            >
              <BurgerMenu />
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </>
  );
};
